import type { AxiosError } from 'axios'
import { apiClient } from './client'
import type {
  PaginatedResponse,
  TestTemplate,
  Test,
  TestAnswer,
  TestItem,
  TestStatus,
} from '@/types'

type ApiResponse<T> = {
  success?: boolean
  message?: string
  data?: T
}

type IndexResponse<T> =
  | T[]
  | {
      /** Плоский список или вложенный Laravel LengthAwarePaginator в `data`. */
      data?: T[] | LaravelPaginatorShape<T>
      meta?: {
        current_page?: number
        last_page?: number
        per_page?: number
        total?: number
      }
      current_page?: number
      last_page?: number
      per_page?: number
      total?: number
      message?: string
    }

/** Сериализация paginate() из Laravel внутри обёртки `{ data: paginator }`. */
type LaravelPaginatorShape<T> = {
  data?: T[]
  current_page?: number
  last_page?: number
  per_page?: number
  total?: number
  meta?: { current_page?: number; last_page?: number; per_page?: number; total?: number }
}

function isLaravelPaginatorObject<T>(x: unknown): x is LaravelPaginatorShape<T> {
  return (
    x !== null &&
    typeof x === 'object' &&
    'data' in x &&
    Array.isArray((x as LaravelPaginatorShape<T>).data)
  )
}

function unwrapPaginated<T>(raw: IndexResponse<T>): PaginatedResponse<T> {
  if (Array.isArray(raw)) {
    return {
      data: raw,
      current_page: 1,
      last_page: 1,
      per_page: 20,
      total: raw.length,
    }
  }

  if (raw?.data && Array.isArray(raw.data)) {
    const meta = raw.meta ?? {}
    return {
      data: raw.data,
      current_page: meta.current_page ?? raw.current_page ?? 1,
      last_page: meta.last_page ?? raw.last_page ?? 1,
      per_page: meta.per_page ?? raw.per_page ?? 20,
      total: meta.total ?? raw.total ?? 0,
    }
  }

  // GET /tests: { data: { data: [...], current_page, ... } }
  if (raw?.data && isLaravelPaginatorObject<T>(raw.data)) {
    const page = raw.data
    const meta = page.meta ?? {}
    const rows = page.data ?? []
    return {
      data: rows,
      current_page: meta.current_page ?? page.current_page ?? 1,
      last_page: meta.last_page ?? page.last_page ?? 1,
      per_page: meta.per_page ?? page.per_page ?? 20,
      total: meta.total ?? page.total ?? rows.length,
    }
  }

  return {
    data: [],
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
  }
}

function unwrapResource<T>(raw: ApiResponse<T> | T): T {
  if (raw && typeof raw === 'object' && 'data' in raw) {
    return (raw as ApiResponse<T>).data as T
  }
  return raw as T
}

export type ListTestsParams = {
  page?: number
  per_page?: number
  status?: TestStatus[]
  /** self|teacher|all */
  source?: 'self' | 'teacher' | 'all'
  lesson_id?: number
  user_id?: number
  /** Started-at date range (YYYY-MM-DD) */
  from?: string
  to?: string
  sort?: 'id' | 'status' | 'started_at' | 'ended_at' | 'results_score'
  order?: 'asc' | 'desc'
}

/** Снимок карточек из превью для создания теста 1:1 с отображаемым набором. */
export type TestPreviewCardSnapshot = {
  id: number
  threshold_id?: number | null
  hit_threshold?: boolean
  prompt_type: string
  answer_type: string
  primary_skill_type?: string | null
  skill_type?: string[]
}

/**
 * Тело `settings` для POST `/tests` (FormRequest `StoreTestRequest`):
 * те же поля, что POST `/tests/preview` ({@link TestPreviewRunRequestPayload}),
 * плюс опционально снимок превью при создании из урока.
 */
export type StoreTestSettingsPayload = TestPreviewRunRequestPayload & {
  preview_user_id?: number | null
  preview_cards?: TestPreviewCardSnapshot[]
}

export type TestTemplatePayload = {
  title: string
  description?: string | null
  settings: Record<string, unknown>
}

/** Тело POST `/tests/preview` (плоский JSON, см. `PreviewTestRequest` на бэкенде). */
export type TestPreviewAttributeCondition = {
  field: string
  operator: string
  value: string
}

export type TestPreviewRunRequestPayload = {
  /** Для превью из урока — id урока; включает проверку подписки (как POST /tests с lesson_id). Не передавать в `settings` при создании. */
  lesson_id?: number
  target_id: number
  target_type: 'user' | 'group'
  exclude_duplicate_words: boolean
  enabled_prompt_type: string[]
  enabled_answer_type: string[]
  auto_check_enabled: boolean
  filters: {
    relax_filter: boolean
    tag_ids: number[]
    attribute_conditions: TestPreviewAttributeCondition[]
  }
  completion_method: {
    mode: 'series' | 'classic' | 'timed'
    series_target?: string
    duration_min?: string
  }
  help_settings: {
    allow_hints: boolean
    allow_transcription: boolean
  }
  explicit_card_ids: number[]
  count_cards?: number
}

type PreviewCardSourceExtras = {
  prompt_type?: string | null
  answer_type?: string | null
  may_show_hints?: boolean
  may_show_transcription?: boolean
  evaluated_skill_types?: string[] | null
  primary_skill_type?: string | null
}

/** Источник попадания карточки в предпросмотр теста (синхронно с TestPreviewService). */
export type PreviewCardSource =
  | ({
      kind: 'threshold'
      progress_from: number
      progress_to: number
      threshold_name?: string
      score?: number
      skill_type?: string[]
    } & PreviewCardSourceExtras)
  | ({ kind: 'fallback'; score?: number; skill_type?: string[] } & PreviewCardSourceExtras)
  | ({ kind: 'relaxed'; pass: 2 | 3; score?: number; skill_type?: string[] } & PreviewCardSourceExtras)
  | ({ kind: 'unknown' } & PreviewCardSourceExtras)

export function normalizeCardPreviewSources(
  raw: Record<string, PreviewCardSource> | undefined,
): Record<number, PreviewCardSource> {
  if (!raw) return {}
  const out: Record<number, PreviewCardSource> = {}
  for (const [k, v] of Object.entries(raw)) {
    const id = Number(k)
    if (Number.isFinite(id) && v && typeof v === 'object' && 'kind' in v) {
      out[id] = v
    }
  }
  return out
}

export type PreviewResponse = {
  data: {
    users: Array<{
      user_id: number
      count_need: number
      count_found: number
      shortage: number
      threshold: Array<{
        id: number
        name: string
        progress_from: number
        progress_to: number
        count_cards_in_test: number
      }>
      cards: Array<{
        id: number
        text: string
        translation: string
        relax_filters: string[]
        threshold_id: number | null
        hit_threshold: boolean
        prompt_type: string
        answer_type: string
        skill_type: string[]
      }>
    }>
  }
}

export const testsApi = {
  list: async (params: ListTestsParams = {}): Promise<PaginatedResponse<Test>> => {
    const response = await apiClient.get<IndexResponse<Test>>('/tests', {
      params,
      paramsSerializer: {
        indexes: null,
        serialize: (p: Record<string, unknown>) => {
          const sp = new URLSearchParams()
          Object.keys(p).forEach((key) => {
            const value = p[key]
            if (value === undefined || value === null) return
            if (Array.isArray(value)) {
              value.forEach((item) => sp.append(`${key}[]`, String(item)))
              return
            }
            sp.append(key, String(value))
          })
          return sp.toString()
        },
      },
    })
    return unwrapPaginated(response.data)
  },

  listTemplates: async (): Promise<TestTemplate[]> => {
    try {
      const response = await apiClient.get<{ data: TestTemplate[] }>('/test-templates')
      return response.data.data ?? []
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string; error?: string }>
      const errorMessage =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        axiosError.message ||
        'Ошибка загрузки шаблонов'
      throw new Error(errorMessage)
    }
  },

  createTemplate: async (payload: TestTemplatePayload): Promise<TestTemplate> => {
    try {
      const response = await apiClient.post<{ message?: string; data?: TestTemplate }>('/test-templates', payload)
      if (response.data?.data) return response.data.data
      throw new Error(response.data?.message || 'Ошибка сохранения шаблона')
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string; error?: string }>
      const errorMessage =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        axiosError.message ||
        'Ошибка сохранения шаблона'
      throw new Error(errorMessage)
    }
  },

  deleteTemplate: async (templateId: number): Promise<void> => {
    try {
      await apiClient.delete(`/test-templates/${templateId}`)
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string; error?: string }>
      const errorMessage =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        axiosError.message ||
        'Ошибка удаления шаблона'
      throw new Error(errorMessage)
    }
  },

  preview: async (payload: TestPreviewRunRequestPayload): Promise<PreviewResponse['data']> => {
    try {
      const response = await apiClient.post<PreviewResponse>('/tests/preview', payload)
      return response.data.data
    } catch (error) {
      const axiosError = error as AxiosError<{
        message?: string
        error?: string
        parameter?: string
      }>
      const status = axiosError.response?.status
      const data = axiosError.response?.data
      const errorMessage =
        data?.message ||
        data?.error ||
        axiosError.message ||
        'Ошибка предпросмотра теста'
      const err = new Error(errorMessage) as Error & { status?: number; code?: string; parameter?: string }
      if (status !== undefined) err.status = status
      if (data?.error) err.code = data.error
      if (data?.parameter) err.parameter = data.parameter
      throw err
    }
  },

  createSelf: async (settings: StoreTestSettingsPayload): Promise<{ id: number; message?: string }> => {
    try {
      const response = await apiClient.post<{ message?: string; data?: { id: number } }>(
        '/tests',
        { settings },
      )
      if (response.data?.data?.id) return { id: response.data.data.id, message: response.data.message }
      throw new Error(response.data?.message || 'Ошибка создания теста')
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string; error?: string }>
      const errorMessage =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        axiosError.message ||
        'Ошибка создания теста'
      throw new Error(errorMessage)
    }
  },

  createFromLesson: async (
    lessonId: number,
    settings: StoreTestSettingsPayload,
    prepMinutesBeforeStart = 0,
  ): Promise<{ id: number; message?: string }> => {
    try {
      const response = await apiClient.post<{ message?: string; data?: { id: number } }>('/tests', {
        lesson_id: lessonId,
        prep_minutes_before_start: prepMinutesBeforeStart,
        settings,
      })
      if (response.data?.data?.id) return { id: response.data.data.id, message: response.data.message }
      throw new Error(response.data?.message || 'Ошибка создания теста к уроку')
    } catch (error) {
      const axiosError = error as AxiosError<{
        message?: string
        error?: string
        parameter?: string
      }>
      const status = axiosError.response?.status
      const data = axiosError.response?.data
      const errorMessage =
        data?.message ||
        data?.error ||
        axiosError.message ||
        'Ошибка создания теста к уроку'
      const err = new Error(errorMessage) as Error & { status?: number; code?: string; parameter?: string }
      if (status !== undefined) err.status = status
      if (data?.error) err.code = data.error
      if (data?.parameter) err.parameter = data.parameter
      throw err
    }
  },

  get: async (testId: number): Promise<Test> => {
    try {
      const response = await apiClient.get<Test | { data?: Test }>(`/tests/${testId}`)
      return unwrapResource<Test>(response.data)
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string; error?: string }>
      const errorMessage =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        axiosError.message ||
        'Ошибка загрузки теста'
      throw new Error(errorMessage)
    }
  },

  /**
   * Следующая карточка прогона.
   *
   * `fromItemId` — якорь: позиция, от которой двигается клиент. С ним сервер
   * отличает повтор запроса от честного продвижения и не перескакивает через
   * слово (EA-494).
   */
  getNextItem: async (testId: number, fromItemId?: number | null): Promise<TestItem | null> => {
    try {
      const response = await apiClient.post<{ data: TestItem | null }>(
        `/tests/${testId}/next-item`,
        fromItemId != null && Number.isFinite(fromItemId) ? { from_item_id: fromItemId } : {},
      )
      return response.data.data
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string; error?: string }>
      const errorMessage =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        axiosError.message ||
        'Ошибка переключения на следующую карточку'
      throw new Error(errorMessage)
    }
  },

  submitAnswer: async (
    testId: number,
    itemId: number,
    payload: {
      skill_type?: string
      answer_type: string
      answer_payload?: Record<string, unknown> | null
    },
  ): Promise<{ answers: TestAnswer[] }> => {
    try {
      const response = await apiClient.post<ApiResponse<{ answers: TestAnswer[] }>>(
        `/tests/${testId}/items/${itemId}/answers`,
        payload,
      )
      const d = response.data?.data
      if (d && Array.isArray(d.answers)) return { answers: d.answers }
      throw new Error(response.data?.message || 'Ошибка отправки ответа')
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string; error?: string }>
      const errorMessage =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        axiosError.message ||
        'Ошибка отправки ответа'
      throw new Error(errorMessage)
    }
  },

  sendItemEvent: async (
    testId: number,
    itemId: number,
    event: 'hint_opened' | 'transcription_opened' | 'audio_played' | 'answer_draft' | 'hint_created',
    payload?: Record<string, unknown>,
  ): Promise<TestItem> => {
    try {
      const response = await apiClient.post<ApiResponse<TestItem>>(
        `/tests/${testId}/items/${itemId}/events`,
        { event, payload: payload ?? undefined },
      )
      if (response.data?.data) return response.data.data
      throw new Error(response.data?.message || 'Ошибка события карточки')
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string; error?: string }>
      const errorMessage =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        axiosError.message ||
        'Ошибка события карточки'
      throw new Error(errorMessage)
    }
  },

  /** Устный ответ (speech_*): создать строки test_answers без ответа ученика — оценку ставит учитель на уроке. */
  teacherEvaluate: async (
    testId: number,
    itemId: number,
    payload: { answer_id: number; is_correct: boolean },
  ): Promise<TestAnswer> => {
    try {
      const response = await apiClient.post<ApiResponse<TestAnswer>>(
        `/tests/${testId}/items/${itemId}/teacher-evaluation`,
        payload,
      )
      if (response.data?.data) return response.data.data
      throw new Error(response.data?.message || 'Ошибка оценки ответа')
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string; error?: string }>
      const errorMessage =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        axiosError.message ||
        'Ошибка оценки ответа'
      throw new Error(errorMessage)
    }
  },

  teacherAdvanceParticipant: async (testId: number, participantId: number): Promise<TestItem | null> => {
    try {
      const response = await apiClient.post<{ data: TestItem | null }>(
        `/tests/${testId}/next-item`,
        { participant_id: participantId },
      )
      return response.data.data
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string; error?: string }>
      const errorMessage =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        axiosError.message ||
        'Ошибка переключения карточки'
      throw new Error(errorMessage)
    }
  },

  complete: async (testId: number): Promise<{ id: number; status: string; message?: string }> => {
    try {
      const response = await apiClient.post<{ message?: string; data?: { id: number; status: string } }>(
        `/tests/${testId}/complete`,
        {},
      )
      if (response.data?.data) return { ...response.data.data, message: response.data.message }
      throw new Error(response.data?.message || 'Ошибка завершения теста')
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string; error?: string }>
      const errorMessage =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        axiosError.message ||
        'Ошибка завершения теста'
      throw new Error(errorMessage)
    }
  },

  // listUserTests: replaced by list({ user_id, ... })
}

